import React, { useState, useCallback } from 'react';
import { FaLock } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { FiShield } from "react-icons/fi";
import logo from '../Assets/logogeniac.png';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { checkEmailSSO, getSSOInitiateURL, type SSOCheckResult } from '../../api/sso';

function LoginForm() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoInfo, setSsoInfo] = useState<SSOCheckResult | null>(null);
  const [checkingSSO, setCheckingSSO] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
    if (name === 'email') setSsoInfo(null);
  };

  const handleEmailBlur = useCallback(async () => {
    const email = formData.email.trim();
    if (!email || !email.includes('@')) return;
    setCheckingSSO(true);
    try {
      const result = await checkEmailSSO(email);
      setSsoInfo(result.sso_enabled ? result : null);
    } catch {
      setSsoInfo(null);
    } finally {
      setCheckingSSO(false);
    }
  }, [formData.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email.trim()) {
      setError(t('login.errors.emailRequired'));
      return;
    }

    if (!formData.password) {
      setError(t('login.errors.passwordRequired'));
      return;
    }

    try {
      setLoading(true);
      await login(formData.email, formData.password);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || t('login.errors.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1c2c] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <div className="flex justify-center mb-4">
          <img src={logo} alt="GenIaC Logo" className="h-16" />
        </div>
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">{t('login.title')}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">{t('login.fields.email.label')}</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                placeholder={t('login.fields.email.placeholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                disabled={loading}
              />
              <MdEmail className="absolute left-3 top-3 text-gray-400" />
              {checkingSSO && (
                <div className="absolute right-3 top-2.5 w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          </div>

          {/* SSO detected */}
          {ssoInfo?.sso_enabled && ssoInfo.organization_id ? (
            <div className="mb-4">
              <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-center gap-2">
                <FiShield size={13} />
                SSO activé pour <strong>{ssoInfo.domain}</strong>
                &nbsp;({ssoInfo.protocol?.toUpperCase()})
              </div>
              <a
                href={getSSOInitiateURL(ssoInfo.organization_id)}
                className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-300 text-sm font-medium"
              >
                <FiShield size={16} />
                Se connecter avec SSO
              </a>
              <button
                type="button"
                onClick={() => setSsoInfo(null)}
                className="mt-2 w-full text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Utiliser email / mot de passe à la place
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">{t('login.fields.password.label')}</label>
                <div className="relative">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t('login.fields.password.placeholder')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                    disabled={loading}
                  />
                  <FaLock className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>

              <div className="flex justify-between items-center text-sm mb-6">
                <label className="flex items-center gap-2 text-gray-600">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="accent-blue-600"
                    disabled={loading}
                  />
                  {t('login.rememberMe')}
                </label>
                <Link to="/forgot-password" className="text-blue-600 hover:underline">
                  {t('login.forgotPassword')}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-300 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {loading ? t('login.submitting') : t('login.submit')}
              </button>
            </>
          )}
        </form>

        <p className="mt-4 text-center text-sm text-gray-600">
          {t('login.noAccount')} <Link to="/register" className="text-blue-600 hover:underline">{t('login.createAccount')}</Link>
        </p>
      </div>
    </div>
  );
}

export default LoginForm;
